import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Password reset request. Backend generates a Supabase recovery link and delivers
 * it via Resend. The endpoint responds identically whether the email exists or not
 * to prevent enumeration.
 */
export default function Reset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-reset", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative flex flex-col px-6 py-8 sm:px-10">
      {/* Same full-screen luxury photo used on Login — keeps the brand
          promise consistent across every auth-y screen. */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1400&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/35 to-slate-950/85" />
      </div>

      <div className="relative z-10 pt-6 pb-8 text-center text-white">
        <div className="inline-flex items-center gap-2.5 mb-4">
          <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-white/25" />
          <span className="font-heading text-xl font-semibold tracking-tight drop-shadow">iKEY</span>
        </div>
        <h2 className="font-heading text-[26px] sm:text-3xl leading-[1.1] font-bold tracking-tight px-2 drop-shadow-lg">
          iKEY: The Only Key You Need
          <span className="block mt-1 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
            from Lead to Sold.
          </span>
        </h2>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center">
        <div className="w-full max-w-md rounded-2xl bg-white/30 backdrop-blur-xl p-6 sm:p-8 shadow-2xl ring-1 ring-white/30">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-white drop-shadow-md" data-testid="reset-title">Reset your password</h1>
          <p className="mt-2 text-sm text-white/90 drop-shadow">
            Enter the email tied to your iKEY account. If it exists, we'll send a reset link.
          </p>

          {sent ? (
            <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="reset-sent">
              If <b>{email}</b> is registered, you'll receive an email with a reset link within a minute. Check spam if it doesn't arrive.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-white drop-shadow">Email</span>
                <input
                  type="email" required value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand"
                  data-testid="reset-email-input"
                />
              </label>
              <button
                type="submit" disabled={busy}
                className="w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "var(--primary-hex)" }}
                data-testid="reset-submit"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          <div className="mt-6 text-xs text-white/85 drop-shadow">
            <Link to="/login" className="hover:text-white">← Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
